// aHedge investor dashboard — one scrolling page. The sidebar nav
// scroll-spies these four sections (overview / performance / positions /
// pnl). All data scopes to the shared strategy filter.
import type { ReactNode } from 'react';
import {
  usePortfolioPnl,
  usePortfolioPositions,
  usePortfolioSummary,
} from '../../hooks/useApi';
import type { PortfolioClosedPosition, PortfolioLivePosition } from '../../lib/api';
import { PortfolioNavChart } from '../../components/charts/portfolio-nav-chart';
import { StrategyFilter } from '../../components/investor/strategy-filter';
import { cn, formatCurrency, formatDateShort, formatPercent } from '../../lib/utils';
import { Card, Kpi, MoneyRow, pnlColor } from './kit';

function Section({
  id,
  children,
  className,
}: {
  id: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section id={id} className={cn('scroll-mt-6', className)}>
      {children}
    </section>
  );
}

function SectionHeading({ eyebrow, title }: { eyebrow: string; title: string }) {
  return (
    <div className="mb-4">
      <p className="text-xs font-semibold uppercase tracking-wide text-blue-600">{eyebrow}</p>
      <h2 className="text-xl font-bold text-slate-900">{title}</h2>
    </div>
  );
}

function signed(v: number | null | undefined): string {
  if (v == null) return '—';
  return `${v > 0 ? '+' : ''}${formatCurrency(v)}`;
}

// ── Positions ──────────────────────────────────────────────────────────
function tickToPrice(tick: number): number {
  return Math.pow(1.0001, tick) * 1e12;
}

function priceBand(p: PortfolioLivePosition): { lower: number; upper: number } | null {
  const cur = p.currentPrice;
  if (p.mintTickLower != null && p.mintTickUpper != null) {
    const a = tickToPrice(p.mintTickLower);
    const b = tickToPrice(p.mintTickUpper);
    const lower = Math.min(a, b);
    const upper = Math.max(a, b);
    if (cur != null && cur > 0 && lower < cur * 3 && upper > cur / 3) return { lower, upper };
  }
  if (cur != null && cur > 0) {
    return { lower: cur * (1 - p.rangePct / 100), upper: cur * (1 + p.rangePct / 100) };
  }
  return null;
}

function priceFmt(v: number): string {
  return v >= 100 ? `$${v.toFixed(0)}` : `$${v.toFixed(4)}`;
}

function RangeBar({ p }: { p: PortfolioLivePosition }) {
  const band = priceBand(p);
  if (!band || p.currentPrice == null) return null;
  const span = band.upper - band.lower;
  const pos = span > 0 ? ((p.currentPrice - band.lower) / span) * 100 : 50;
  const clamped = Math.max(0, Math.min(100, pos));
  const inRange = pos >= 0 && pos <= 100;
  return (
    <div className="mt-4">
      <div className="relative h-1.5 rounded-full bg-gradient-to-r from-amber-300 via-slate-200 to-amber-300">
        <div
          className={cn(
            'absolute top-1/2 h-3.5 w-3.5 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white shadow',
            inRange ? 'bg-blue-600' : 'bg-rose-500',
          )}
          style={{ left: `${clamped}%` }}
        />
      </div>
      <div className="mt-1.5 flex justify-between text-xs tabular-nums text-slate-500">
        <span>{priceFmt(band.lower)}</span>
        <span className="font-medium text-slate-700">{priceFmt(p.currentPrice)}</span>
        <span>{priceFmt(band.upper)}</span>
      </div>
    </div>
  );
}

function LiveCard({ p }: { p: PortfolioLivePosition }) {
  const rows: { label: string; node: ReactNode }[] = [
    { label: 'Capital deployed', node: formatCurrency(p.deployedCapitalUsd) },
    { label: 'Net value', node: formatCurrency(p.netValueUsd) },
    {
      label: 'Current P&L',
      node: (
        <span className={pnlColor(p.pnlUsd)}>
          {signed(p.pnlUsd)}
          {p.pnlPct != null && <span className="ml-1 text-xs">({formatPercent(p.pnlPct)})</span>}
        </span>
      ),
    },
    {
      label: 'Annualized ROI',
      node: (
        <span className={pnlColor(p.annualizedRoiPct)}>
          {p.annualizedRoiPct != null ? formatPercent(p.annualizedRoiPct) : '—'}
        </span>
      ),
    },
  ];
  return (
    <Card>
      <div className="mb-3 flex items-baseline justify-between">
        <h3 className="text-base font-semibold text-slate-900">{p.baseSymbol} / USDC</h3>
        <span className="text-xs text-slate-400">±{p.rangePct}% range</span>
      </div>
      <div className="divide-y divide-slate-100">
        {rows.map((r) => (
          <div key={r.label} className="flex items-center justify-between py-2.5 text-sm">
            <span className="text-slate-500">{r.label}</span>
            <span className="font-semibold tabular-nums text-slate-900">{r.node}</span>
          </div>
        ))}
      </div>
      <RangeBar p={p} />
    </Card>
  );
}

function ClosedRow({ c }: { c: PortfolioClosedPosition }) {
  return (
    <div className="grid grid-cols-2 items-center gap-2 border-b border-slate-100 py-4 last:border-0 sm:grid-cols-4">
      <div>
        <p className="font-semibold text-slate-900">{c.baseSymbol} / USDC</p>
        <p className="text-xs text-slate-400">
          {formatDateShort(c.startTs)} – {c.endTs ? formatDateShort(c.endTs) : '—'}
        </p>
      </div>
      <div className="text-sm">
        <p className="text-xs text-slate-400">Started</p>
        <p className="tabular-nums text-slate-700">{formatCurrency(c.startValueUsd)}</p>
      </div>
      <div className="text-sm">
        <p className="text-xs text-slate-400">Ended</p>
        <p className="tabular-nums text-slate-700">{formatCurrency(c.endValueUsd)}</p>
      </div>
      <div className="text-right">
        <p className="text-xs text-slate-400">Profit</p>
        <p className={cn('font-semibold tabular-nums', pnlColor(c.profitUsd))}>{signed(c.profitUsd)}</p>
      </div>
    </div>
  );
}

function PositionsSection() {
  const { data, isLoading } = usePortfolioPositions();
  return (
    <>
      <SectionHeading eyebrow="Current positions" title="Live liquidity pools" />
      {isLoading ? (
        <p className="text-sm text-slate-400">Loading…</p>
      ) : data && data.live.length > 0 ? (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          {data.live.map((p) => (
            <LiveCard key={p.strategyId} p={p} />
          ))}
        </div>
      ) : (
        <Card>
          <p className="text-sm text-slate-400">No live positions.</p>
        </Card>
      )}

      <div className="mt-10">
        <SectionHeading eyebrow="Position history" title="Closed positions" />
        <Card>
          {data && data.closed.length > 0 ? (
            data.closed.map((c) => <ClosedRow key={`${c.strategyId}-${c.startTs}`} c={c} />)
          ) : (
            <p className="text-sm text-slate-400">No closed positions yet.</p>
          )}
        </Card>
      </div>
    </>
  );
}

// ── Total P&L ──────────────────────────────────────────────────────────
function PnlSection() {
  const { data } = usePortfolioPnl();
  return (
    <>
      <SectionHeading eyebrow="Historical performance" title="Total P&L" />
      <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Kpi
          label="Total P&L"
          value={signed(data?.totalPnlUsd)}
          valueClass={
            data ? (data.totalPnlUsd >= 0 ? 'text-emerald-400' : 'text-rose-400') : undefined
          }
          accent
        />
        <Kpi
          label="Annualized ROI"
          value={data?.annualizedRoiPct != null ? formatPercent(data.annualizedRoiPct) : '—'}
          valueClass={pnlColor(data?.annualizedRoiPct)}
        />
      </div>
      <Card>
        <div className="divide-y divide-slate-100">
          <MoneyRow
            label="LP fees + rewards"
            hint="Swap fees + AERO incentives"
            value={data?.lpFeesRewardsUsd}
          />
          <MoneyRow
            label="Hedge results"
            hint="Perp funding + realized + unrealized"
            value={data?.hedgeResultsUsd}
          />
          <MoneyRow
            label="Hedge trading fees"
            hint="Taker fees on the perp"
            value={data != null ? -data.hlFeesUsd : null}
          />
          <MoneyRow
            label="Market movement & IL"
            hint="Price drift + impermanent loss + residual"
            value={data?.marketIlUsd}
          />
          <MoneyRow label="Total P&L" value={data?.totalPnlUsd} bold />
        </div>
      </Card>
      <Card className="mt-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="font-medium text-slate-700">Gas paid</p>
            <p className="text-xs text-slate-400">
              From the gas reserve — outside your capital, does not reduce P&L.
            </p>
          </div>
          <p className="tabular-nums font-medium text-slate-500">
            {data != null ? formatCurrency(-data.gasUsd) : '—'}
          </p>
        </div>
      </Card>
    </>
  );
}

// ── Page ───────────────────────────────────────────────────────────────
export default function InvestorDashboard() {
  const { data: summary } = usePortfolioSummary();
  return (
    <>
      <Section id="overview">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Kpi label="Current net value" value={formatCurrency(summary?.netValueUsd)} accent />
          <Kpi label="Total capital deployed" value={formatCurrency(summary?.deployedCapitalUsd)} />
          <Kpi
            label="Current P&L"
            value={signed(summary?.pnlUsd)}
            valueClass={pnlColor(summary?.pnlUsd)}
            sub={
              summary?.pnlPct != null ? (
                <span className={pnlColor(summary.pnlPct)}>
                  {formatPercent(summary.pnlPct)} vs deployed
                </span>
              ) : undefined
            }
          />
          <StrategyFilter />
        </div>
      </Section>

      <Section id="performance" className="mt-12">
        <PortfolioNavChart />
      </Section>

      <Section id="positions" className="mt-12">
        <PositionsSection />
      </Section>

      <Section id="pnl" className="mt-12">
        <PnlSection />
      </Section>
    </>
  );
}
